import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useImagesContext } from '../../contexts/ImagesContext';
import {
  getCompetitors,
  getTeachers,
  getMatchingTest,
} from '../../services/matching';
import { useDebounce } from '../../utils/useDebounce';
import { DivClick, ImgClick } from '../utility';

const SelectCompetitorDialog = ({ student, onSelectCompetitor }) => {
  const { images } = useImagesContext();
  const [teachers, setTeachers] = React.useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = React.useState('');
  const debouncedTeacherSearchTerm = useDebounce(teacherSearchTerm, 500);
  const [teacherSearchResults, setTeacherSearchResults] = React.useState([]);
  const [competitors, setCompetitors] = React.useState([]);
  const [competitorSearchTerm, setCompetitorSearchTerm] = React.useState('');
  const debouncedCompetitorSearchTerm = useDebounce(competitorSearchTerm, 500);
  const [competitorSearchResults, setCompetitorSearchResults] = React.useState(
    [],
  );
  const [selectcompetitor, setSelectCompetitor] = React.useState(null);
  const [isTest, setIsTest] = React.useState(false);
  const [canTest, setCanTest] = React.useState(false);
  const [teacherPageIndex, setTeacherPageIndex] = React.useState(0);
  const [competitorPageIndex, setCompetitorPageIndex] = React.useState(0);
  const TEACHER_PER_PAGE = 5;
  const COMPETITOR_PER_PAGE = 10;

  useEffect(() => {
    getCompetitors(student.id).then((response) => {
      setCompetitors(response.data);
      setCompetitorSearchResults(response.data);
    });
    getTeachers(student.id).then((response) => {
      setTeachers(response.data);
      setTeacherSearchResults(response.data);
    });
    getMatchingTest(student.id).then((response) => {
      if (response.data.canTest === true) {
        setCanTest(true);
      } else {
        setCanTest(false);
      }
    });
  }, [student.level]);

  const Teachers = teacherSearchResults.slice(teacherPageIndex * TEACHER_PER_PAGE, (teacherPageIndex + 1) * TEACHER_PER_PAGE).map((teacher) => (
    <TeacherItemBlock
      style={
        teacher.id === selectcompetitor?.id
          ? { filter: 'drop-shadow(0px 4px 4px black)' }
          : {}
      }
      key={`teacher_${teacher.id}`}
      onClick={() => setSelectCompetitor(teacher)}
    >
      <TeacherImage src={teacher.avatar} alt={teacher.name} />
      <TeacherNameBackground />
      <TeacherName>{teacher.name}</TeacherName>
    </TeacherItemBlock>
  ));

  const Competitors = competitorSearchResults.slice(competitorPageIndex * COMPETITOR_PER_PAGE, (competitorPageIndex + 1) * COMPETITOR_PER_PAGE).map((competitor) => (
    <TeacherItemBlock
      style={
        competitor.id === selectcompetitor?.id
          ? { filter: 'drop-shadow(0px 4px 4px black)' }
          : {}
      }
      key={`competitor_${competitor.id}`}
      onClick={() => setSelectCompetitor(competitor)}
    >
      <TeacherImage src={competitor.avatar} alt={competitor.name} />
      <TeacherNameBackground />
      <TeacherName>{competitor.name}</TeacherName>
    </TeacherItemBlock>
  ));

  const handleTeacherSearchChange = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setTeacherSearchTerm(searchTerm);
  };

  useEffect(() => {
    if (debouncedTeacherSearchTerm === '') {
      setTeacherSearchResults(teachers);
      return;
    }
    const filteredTeachers = teachers.filter((teacher) =>
      teacher.name.toLowerCase().includes(debouncedTeacherSearchTerm),
    );
    setTeacherSearchResults(filteredTeachers);
  }, [debouncedTeacherSearchTerm, teachers]);

  const handleCompetitorSearchChange = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setCompetitorSearchTerm(searchTerm);
  };

  useEffect(() => {
    if (debouncedCompetitorSearchTerm === '') {
      setCompetitorSearchResults(competitors);
      return;
    }
    const filteredCompetitors = competitors.filter((competitor) =>
      competitor.name.toLowerCase().includes(debouncedCompetitorSearchTerm),
    );
    setCompetitorSearchResults(filteredCompetitors);
    setCompetitorPageIndex(0);
  }, [debouncedCompetitorSearchTerm, competitors]);

  return (
    <Container>
      <BackgroundImage
        src={images['select-background.png']}
        alt="Select Background"
      />
      <ButtonOkImage
        src={images['select-button-ok.png']}
        onClick={() => {
          // onSelectPlayer2(player2List[0]);
          onSelectCompetitor(selectcompetitor, isTest);
        }}
      />
      <SearchContainer style={{ position: 'absolute', top: 100, left: 700 }}>
        <SearchImage src={images['select-search.png']} alt="Search" />
        <SearchText
          name="teacher"
          placeholder="Search..."
          onChange={handleTeacherSearchChange}
        />
      </SearchContainer>
      <TestImage
        src={images[`select-test-${canTest ? 'on' : 'off'}.png`]}
        alt="Test On"
      />
      <TestCheckImage
        onClick={() => {
          if (canTest) {
            setIsTest(!isTest);
          }
        }}
        src={images[`select-test-box-${isTest ? 'on' : 'off'}.png`]}
        alt="Test Check Off"
      />
      <TeacherContainer>
        <TeacherBlock>{Teachers}</TeacherBlock>
      </TeacherContainer>
      <SearchContainer style={{ position: 'absolute', top: 445, left: 700 }}>
        <SearchImage src={images['select-search.png']} alt="Search" />
        <SearchText
          name="competitor"
          placeholder="Search..."
          onChange={handleCompetitorSearchChange}
        />
      </SearchContainer>
      <CompetitorContainer>
        <CompetitorBlock>{Competitors}</CompetitorBlock>
      </CompetitorContainer>
      {competitorSearchResults.length > COMPETITOR_PER_PAGE && competitorPageIndex > 0 && (
        <UpButton
          src={images['select-up.png']}
          onClick={() => setCompetitorPageIndex((prev) => Math.max(prev - 1, 0))}
        />
      )}

      {competitorSearchResults.length > COMPETITOR_PER_PAGE && (competitorPageIndex + 1) * COMPETITOR_PER_PAGE < competitorSearchResults.length && (
        <DownButton
          src={images['select-down.png']}
          onClick={() => setCompetitorPageIndex((prev) =>
            Math.min(prev + 1, Math.floor(competitorSearchResults.length / COMPETITOR_PER_PAGE)),
          )}
        />
      )}
      {teacherSearchResults.length > TEACHER_PER_PAGE && teacherPageIndex > 0 && (
        <BackButton
          src={images['select-back.png']}
          onClick={() => setTeacherPageIndex((prev) => Math.max(prev - 1, 0))}
        />
      )}
      {teacherSearchResults.length > TEACHER_PER_PAGE && (teacherPageIndex + 1) * TEACHER_PER_PAGE < teacherSearchResults.length && (
        <ForwardButton
          src={images['select-forward.png']}
          onClick={() => setTeacherPageIndex((prev) =>
            Math.min(prev + 1, Math.floor(teacherSearchResults.length / TEACHER_PER_PAGE)),
          )}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  z-index: 10;
`;

const BackgroundImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  // z-index: 1;
`;

const ButtonOkImage = styled(ImgClick)`
  position: absolute;
  top: 500px;
  left: 370px;
`;

const TestImage = styled.img`
  position: absolute;
  top: 120px;
  left: 1200px;
`;

const TestCheckImage = styled.img`
  position: absolute;
  top: 144px;
  left: 1300px;
`;

const TeacherContainer = styled.div`
  position: absolute;
  top: 250px;
  left: 50%;
  height: 180px;
  transform: translate(-50%);
  width: 850px;
  // border: 2px solid yellow;
  overflow: hidden;
`;

const CompetitorContainer = styled.div`
  position: absolute;
  top: 580px;
  left: 50%;
  height: 350px;
  transform: translate(-50%);
  width: 850px;
  // border: 2px solid yellow;
  overflow: hidden;
`;

const TeacherBlock = styled.div`
  position: absolute;
  gap: 40px;
  display: flex;
  margin-left: 6px;
`;

const CompetitorBlock = styled.div`
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  margin-left: 6px;
`;

const TeacherItemBlock = styled(DivClick)`
  position: relative;
`;

const TeacherNameBackground = styled.div`
  position: absolute;
  // top: -16px;
  top: 0px;
  left: 50%;
  transform: translate(-50%);
  width: 100px;
  height: 25px;
  background-color: #fed6a3;
  border-radius: 16px;
  border: 2px solid #d6b789;
`;

const TeacherName = styled.span`
  position: absolute;
  // top: -12px;
  top: 4px;
  left: 50%;
  transform: translate(-50%);
  width: 100px;
  text-align: center;
  font-family: 'PSLxText';
  font-weight: bold;
  font-size: 24px;
  line-height: 0.8;
  // text-stroke: 2px black;
  -webkit-text-stroke: 2px white;
  paint-order: stroke fill;
`;

const TeacherImage = styled.img`
  position: relative;
  top: 16px;
  width: 135px;
  height: 135px;
  border-radius: 50%;
`;

const SearchContainer = styled.div`
  position: absolute;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
`;

const SearchImage = styled.img`
  position: absolute;
`;

const SearchText = styled.input`
  position: absolute;
  background: transparent;
  top: 0;
  left: 0;
  width: 432px;
  height: 115px;
  border: none;
  outline: none;
  text-align: center;
  font-family: 'PSLxText';
  font-weight: bold;
  font-size: 48px;
  line-height: 0.8;
`;

const BackButton = styled(ImgClick)`
  position: absolute;
  top: 294px;
  left: 442px;
`;

const ForwardButton = styled(ImgClick)`
  position: absolute;
  top: 294px;
  left: 1390px;
`;

const UpButton = styled(ImgClick)`
  position: absolute;
  top: 638px;
  right: 472px;
`;

const DownButton = styled(ImgClick)`
  position: absolute;
  top: 822px;
  right: 472px;
`;

export default SelectCompetitorDialog;
